import * as SentryReact from "@sentry/react";

// Define an interface for Window that includes the optional Sentry object
interface WindowWithSentry extends Window {
  Sentry?: {
    captureException?: typeof SentryReact.captureException;
    // Add other Sentry client methods if needed for checks
  };
}

// Helper to check if React SDK is initialized (basic check)
const isSentryReactReady = () => {
  if (typeof window === "undefined") {
    // Should always be false here, but good practice
    return false;
  }
  const typedWindow = window as WindowWithSentry;
  return !!typedWindow.Sentry?.captureException;
};

interface ClientErrorContext {
  userId?: string;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  source?: string;
  message?: string;
}

/**
 * Client-side error tracking function using @sentry/react.
 */
export function trackClientError(
  error: unknown,
  context?: ClientErrorContext,
): Error {
  const errorObject = error instanceof Error ? error : new Error(String(error));

  // Log client errors in console
  console.error("[Client Error]", errorObject, context);

  // Send to Sentry React if available
  if (isSentryReactReady()) {
    // Set Sentry user context if userId is available
    if (context?.userId) {
      SentryReact.setUser({ id: context.userId });
    } else {
      // Clear user context if no userId is provided for this error
      SentryReact.setUser(null);
    }

    SentryReact.captureException(errorObject, {
      tags: context?.tags,
      extra: context?.extra,
    });
  } else {
    console.warn(
      "[Client Error] Sentry React SDK not ready, cannot capture exception.",
    );
  }

  return errorObject;
}
